import {
  Badge,
  Card,
  Group,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { memo, useMemo } from 'react';
import type { GroupSummary } from '../utils/computeGroupSummary';
import type { SpectralWorkspaceSession } from '../types';
import { collectUniqueSessionDevices } from '../utils/sessionDevice';
import { OverviewKpiGrid } from './OverviewKpiGrid';

type WorkspaceOverviewSectionProps = {
  sessions: SpectralWorkspaceSession[];
  readySessions: SpectralWorkspaceSession[];
  summary: GroupSummary;
  isLoadingSessions?: boolean;
  isMetricsWarming?: boolean;
};

export const WorkspaceOverviewSection = memo(function WorkspaceOverviewSection({
  sessions,
  readySessions,
  summary,
  isLoadingSessions = false,
  isMetricsWarming = false,
}: WorkspaceOverviewSectionProps) {
  const isLoading = isLoadingSessions || isMetricsWarming;
  const devices = useMemo(
    () => collectUniqueSessionDevices(sessions),
    [sessions]
  );

  return (
    <Stack gap='md'>
      <Card withBorder>
        <Group justify='space-between' align='flex-start' wrap='wrap'>
          <div>
            <Title order={4}>Overview workspace</Title>
            {isLoading ? (
              <Stack gap={6} mt={8}>
                <Skeleton height={12} width={320} radius='sm' />
                <Skeleton height={12} width={160} radius='sm' />
              </Stack>
            ) : (
              <Stack gap={4} mt={4}>
                <Text c='dimmed' size='sm'>
                  {`${summary.readySessions} di ${summary.totalSessions} sessioni hanno dati HR e sono usabili per l’analisi sul tratto completo.`}
                </Text>
                {devices.length > 0 ? (
                  <Group gap='xs'>
                    <Text c='dimmed' size='sm'>
                      Sensori:
                    </Text>
                    {devices.map(device => (
                      <Badge key={device} variant='outline' color='gray'>
                        {device}
                      </Badge>
                    ))}
                  </Group>
                ) : null}
              </Stack>
            )}
          </div>
          <Group gap='xs'>
            {isLoading ? (
              <>
                <Skeleton height={22} width={96} radius='xl' />
                <Skeleton height={22} width={110} radius='xl' />
              </>
            ) : (
              <>
                <Badge variant='light'>{sessions.length} nel gruppo</Badge>
                <Badge color='teal' variant='light'>
                  {readySessions.length} con dati HR
                </Badge>
              </>
            )}
          </Group>
        </Group>
      </Card>

      <OverviewKpiGrid summary={summary} isLoading={isLoading} />

      <Group gap='xs'>
        <Badge color='indigo' variant='light'>
          Pipeline HRV preprocessing (NN da HR, 4 Hz)
        </Badge>
        <Text size='sm' c='dimmed'>
          Tachogramma proxy Polar Loop 360; RMSSD da ΔRR; soglia artefatti 5%
          (Kubios-style).
        </Text>
      </Group>
    </Stack>
  );
});
